/**
 * Montagem do motor de sincronizacao.
 *
 * Tres coisas tem de ser verdade ao mesmo tempo para haver sincronizacao:
 * o pacote `@flos/sync` existe, ha um endereco de servidor configurado para
 * esta familia, e ha uma forma de obter um token. Falhando qualquer uma delas,
 * a sincronizacao fica **dormente** — que nao e um erro, e o estado normal de
 * uma familia que ainda nao acabou a instalacao. As escritas continuam a
 * acumular-se na `_outbox` e nenhuma se perde; quando o endereco aparecer, o
 * motor esvazia a fila desde o principio.
 *
 * O endereco NAO e uma constante do codigo. E configuracao por familia: cada
 * casa tem a sua folha de calculo e a sua implantacao. Vive nas configuracoes,
 * como a chave do assistente. (O `VITE_API_URL` do `.env` serve so de valor de
 * partida em desenvolvimento; a definicao ganha sempre.)
 */
import type { FlosCore } from '@flos/core';
import { createDexieStores, deviceId } from './dexieStores';
import type {
  AuthControllerLike,
  SyncEngineLike,
  SyncModulePort,
  SyncSessionLike,
} from './ports';

/** Espaco de nomes das configuracoes da plataforma. Igual ao do assistente. */
export const SYNC_SETTINGS_NAMESPACE = 'core';
/** Endereco do servidor da familia. Nao e segredo — e configuracao. */
export const SYNC_URL_SETTING = 'sync.serverUrl';

/** Porque e que a sincronizacao nao esta ligada, em portugues e sem jargao. */
export type DormantReason =
  | 'sem-pacote'
  | 'sem-servidor'
  | 'sem-transporte'
  | 'sem-conta';

export const DORMANT_MESSAGES: Record<DormantReason, string> = {
  'sem-pacote':
    'A cópia de segurança não está instalada nesta versão. Tudo o que escrever fica salvo no aparelho.',
  'sem-servidor':
    'Ainda não há servidor configurado. Tudo o que escrever fica salvo no aparelho, à espera.',
  'sem-transporte':
    'A ligação ao servidor não está disponível nesta versão. Os seus dados ficam salvos no aparelho.',
  'sem-conta':
    'Para salvar fora do aparelho é preciso entrar com a sua conta. Até lá fica tudo salvo aqui.',
};

export interface BuildSyncEngineOptions {
  core: FlosCore;
  module: SyncModulePort | null;
  /** `null` quando `@flos/auth` nao esta ligado — sem token nao ha sincronizacao. */
  auth: AuthControllerLike | null;
  /** Substitui a definicao. Usado nos testes. */
  url?: string | undefined;
}

export interface BuiltSyncEngine {
  engine: SyncEngineLike | null;
  /** `null` quando o motor arrancou. Caso contrario, porque nao. */
  dormant: DormantReason | null;
}

/**
 * Le o endereco do servidor desta familia.
 * Devolve `''` quando nao esta configurado — e o caso normal, nao um erro.
 */
export function readServerUrl(core: FlosCore): string {
  const stored = core.settings.getSync<string>(SYNC_SETTINGS_NAMESPACE, SYNC_URL_SETTING, '');
  if (typeof stored === 'string' && stored.trim()) return stored.trim();
  const fromEnv = (import.meta.env?.['VITE_API_URL'] as string | undefined) ?? '';
  return typeof fromEnv === 'string' ? fromEnv.trim() : '';
}

export async function saveServerUrl(core: FlosCore, url: string): Promise<void> {
  await core.settings.set(SYNC_SETTINGS_NAMESPACE, SYNC_URL_SETTING, url.trim());
}

/**
 * Constroi o motor, ou explica porque nao. Nunca lança: um motor que nao
 * arranca nao pode impedir ninguem de escrever no celular.
 */
export async function buildSyncEngine(
  options: BuildSyncEngineOptions,
): Promise<BuiltSyncEngine> {
  const { core, module, auth } = options;

  if (!module) return { engine: null, dormant: 'sem-pacote' };
  if (typeof module.AppsScriptTransport !== 'function') {
    return { engine: null, dormant: 'sem-transporte' };
  }

  const url = options.url !== undefined ? options.url.trim() : readServerUrl(core);
  if (!url) return { engine: null, dormant: 'sem-servidor' };

  // Sem forma de provar quem ela e, o servidor recusa tudo. Melhor ficar
  // dormente do que encher a fila de tentativas falhadas ate as parquear.
  if (!auth?.available) return { engine: null, dormant: 'sem-conta' };

  try {
    const stores = createDexieStores(core);
    const device = await deviceId(core);

    const transport = new module.AppsScriptTransport({
      url,
      getToken: () => auth.getIdToken(),
    });

    /**
     * A sessao do motor. Devolver `null` faz o ciclo parar em silencio, e e
     * assim que travamos o envio quando ela nao tem token valido: sem isto, o
     * transporte atirava `NO_TOKEN` a cada volta e, ao final de seis tentativas,
     * o motor parqueava escritas perfeitamente boas.
     */
    const session = (): SyncSessionLike | null => {
      const info = core.session.peek();
      if (!info) return null;
      if (!auth.canSync()) return null;
      return { familyId: info.familyId, memberId: info.memberId, deviceId: device };
    };

    const engine = new module.SyncEngine({
      transport,
      outbox: stores.outbox,
      records: stores.records,
      cursor: stores.cursor,
      conflicts: stores.conflicts,
      snapshots: stores.snapshots,
      events: core.events,
      logger: core.logger,
      session,
      ...(module.browserRuntime ? { runtime: module.browserRuntime() } : {}),
    });

    return { engine, dormant: null };
  } catch (error) {
    core.logger.error('Nao consegui preparar a sincronizacao', {
      erro: error instanceof Error ? error.message : String(error),
    });
    return { engine: null, dormant: 'sem-transporte' };
  }
}
